import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, ExternalLink } from 'lucide-react';

const BlogDetailPage = () => {
  const { id } = useParams();

  // Sample press/media data (you can move this to a separate file or fetch from API)
  const pressItems = {
    'tech-crunch-ai-revolution': {
      publication: 'Tech Crunch India',
      headline: 'Appdroid Tech revolutionizes AI content creation',
      date: 'March 15, 2024',
      author: 'Rajesh Kumar',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa',
      content: `
        <p>In a rapidly evolving digital landscape, Appdroid Tech has emerged as a pioneering force in AI-powered content creation. The Pune-based technology studio has successfully bridged the gap between traditional software development and cutting-edge artificial intelligence.</p>
        
        <h2>The Fusion Scope Innovation</h2>
        <p>At the heart of Appdroid Tech's success is Fusion Scope, their AI content generation vertical. This innovative platform has transformed how brands approach visual storytelling, offering everything from hyperrealistic product photography to cinematic brand films—all generated using advanced AI models.</p>
        
        <p>"We're not just creating content; we're creating experiences that resonate with audiences on a deeper level," says Amit Bhamere, Founder & CEO of Appdroid Tech. "Our AI doesn't replace human creativity—it amplifies it."</p>
        
        <h2>Industry Impact</h2>
        <p>The company has already partnered with major brands including Adani Skills & Education, Saregama, and Chandukaka Saraf Jewels, delivering AI-generated content that rivals traditional production methods at a fraction of the cost and time.</p>
        
        <p>Their work in AI product photography has been particularly revolutionary. Using proprietary algorithms, Fusion Scope can create photorealistic product images with perfect lighting, composition, and brand consistency—all without physical photoshoots.</p>
        
        <h2>Looking Ahead</h2>
        <p>With plans to expand their AI capabilities to video generation and real-time character animation, Appdroid Tech is positioning itself as a leader in the next wave of digital content creation.</p>
        
        <p>The company is currently working on several groundbreaking projects, including AI-powered virtual brand ambassadors and automated content localization for global markets.</p>
      `,
      externalLink: '#',
    },
    'business-standard-fusion-scope': {
      publication: 'Business Standard',
      headline: 'Fusion Scope: The future of brand storytelling',
      date: 'January 20, 2024',
      author: 'Priya Sharma',
      image: 'https://images.unsplash.com/photo-1504639725590-34d0984388bd',
      content: `
        <p>Brand storytelling has entered a new era, and Fusion Scope by Appdroid Tech is leading the charge. This innovative AI platform is transforming how companies create and distribute visual content.</p>
        
        <h2>Breaking Traditional Barriers</h2>
        <p>Traditional video production requires substantial investment in equipment, crew, locations, and post-production. Fusion Scope eliminates these barriers by leveraging artificial intelligence to generate high-quality visual content.</p>
        
        <p>The platform specializes in multiple content formats including:</p>
        <ul>
          <li>AI Product Photography - Hyperrealistic product images with perfect lighting</li>
          <li>Brand Films & TVCs - Cinematic storytelling without physical production</li>
          <li>Character Design - Custom mascots and brand characters</li>
          <li>Fashion & Model Shoots - Editorial-quality imagery with AI-generated models</li>
        </ul>
        
        <h2>Market Response</h2>
        <p>The market response has been overwhelmingly positive. Brands appreciate the speed, cost-effectiveness, and creative flexibility that Fusion Scope offers. What previously took weeks can now be accomplished in days.</p>
        
        <p>"We're seeing a fundamental shift in how brands approach content creation," explains industry analyst Vikram Patel. "AI tools like Fusion Scope aren't just nice-to-have anymore—they're becoming essential for staying competitive."</p>
        
        <h2>Quality Meets Scale</h2>
        <p>One of Fusion Scope's key differentiators is its ability to maintain high quality while scaling production. Brands can generate variations of content for different markets, audiences, and platforms without sacrificing quality or consistency.</p>
      `,
      externalLink: '#',
    },
    'economic-times-pune-startup': {
      publication: 'Economic Times',
      headline: 'How Pune-based startup is transforming digital marketing',
      date: 'November 10, 2023',
      author: 'Ananya Desai',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f',
      content: `
        <p>In the bustling tech hub of Pune, Appdroid Tech has quietly been building something extraordinary. With a dual focus on software development and AI-generated content, the company represents a new breed of technology studios that are redefining digital marketing.</p>
        
        <h2>The Two-Vertical Approach</h2>
        <p>Appdroid Tech operates through two complementary verticals: traditional tech services and AI content creation. This unique combination allows them to offer end-to-end digital solutions that few competitors can match.</p>
        
        <p>Their technology services include:</p>
        <ul>
          <li>Enterprise Software Development</li>
          <li>Mobile App Development (iOS & Android)</li>
          <li>Web Development & Digital Platforms</li>
          <li>Digital Marketing & Performance Ads</li>
          <li>UI/UX Design</li>
        </ul>
        
        <h2>AI Content Revolution</h2>
        <p>But it's their Fusion Scope vertical that's truly turning heads. By leveraging the latest in generative AI, they're creating content that was previously impossible or prohibitively expensive.</p>
        
        <p>"We worked with Fusion Scope for our jewellery campaign," says a spokesperson from Chandukaka Saraf Jewels. "The AI-generated images were so realistic, even our experienced photographers couldn't tell the difference."</p>
        
        <h2>Growing Client Base</h2>
        <p>The company's client roster reads like a who's who of Indian business: Adani, Saregama, Nutrica, and numerous others. This diverse client base speaks to Appdroid Tech's versatility and expertise across industries.</p>
        
        <h2>Future Plans</h2>
        <p>Looking ahead, Appdroid Tech plans to expand its AI capabilities further, with investments in video generation, voice synthesis, and personalized content at scale. The goal is to become India's premier AI content studio.</p>
      `,
      externalLink: '#',
    },
    'yourstory-code-to-cinema': {
      publication: 'YourStory',
      headline: 'From code to cinema: The Appdroid Tech journey',
      date: 'September 5, 2023',
      author: 'Kavya Menon',
      image: 'https://images.unsplash.com/photo-1485988412941-77a35537dae4',
      content: `
        <p>Every startup has an origin story, but few are as compelling as that of Appdroid Tech. What began as a small software development outfit in Pune has evolved into a dual-threat tech powerhouse combining traditional development with revolutionary AI content creation.</p>
        
        <h2>The Founder's Vision</h2>
        <p>Amit Bhamere, founder of Appdroid Tech, saw early on that the future of digital content would be AI-driven. "Seven years ago, when we started, AI content generation was science fiction," he recalls. "But we knew it was coming, and we wanted to be ready."</p>
        
        <p>That foresight led to the creation of Fusion Scope, Appdroid Tech's AI content division, which now generates everything from product photography to full-length brand films.</p>
        
        <h2>The Technical Journey</h2>
        <p>Building Fusion Scope required deep expertise in multiple AI domains:</p>
        <ul>
          <li>Computer Vision for image analysis and generation</li>
          <li>Natural Language Processing for content understanding</li>
          <li>Generative AI models for content creation</li>
          <li>3D Rendering for photorealistic outputs</li>
        </ul>
        
        <p>"The technical challenges were immense," Bhamere admits. "But that's what makes this exciting. We're literally building the future of content creation."</p>
        
        <h2>Client Success Stories</h2>
        <p>The proof is in the results. Brands like Nutrica have used Fusion Scope to create entire marketing campaigns, from social media content to TV commercials, all generated using AI.</p>
        
        <p>"What impressed us most was the speed and flexibility," says a Nutrica marketing executive. "We could iterate on concepts in real-time, something impossible with traditional production."</p>
        
        <h2>Industry Recognition</h2>
        <p>Appdroid Tech's innovative approach hasn't gone unnoticed. The company has received multiple awards for innovation in AI and digital content, cementing its position as an industry leader.</p>
        
        <h2>The Road Ahead</h2>
        <p>As AI technology continues to evolve, so does Appdroid Tech's vision. Plans include expanding into international markets, developing proprietary AI models, and potentially launching a SaaS platform that would democratize AI content creation.</p>
        
        <p>"We're just getting started," Bhamere says with a smile. "The best is yet to come."</p>
      `,
      externalLink: '#',
    },
  };

  const article = pressItems[id];

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black mb-4">Article Not Found</h1>
          <Link to="/recognition" className="text-purple-500 hover:underline">
            Back to Recognition
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page" className="min-h-screen">
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-32 pb-8">
        <Link
          to="/recognition"
          className="inline-flex items-center gap-2 text-purple-500 hover:text-purple-400 transition-colors font-bold"
        >
          <ArrowLeft size={20} />
          Back to Recognition
        </Link>
      </div>

      {/* Hero Image */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="aspect-video rounded-2xl overflow-hidden"
        >
          <img
            src={article.image}
            alt={article.headline}
            className="w-full h-full object-cover"
          />
        </motion.div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 md:px-12 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {/* Meta Info */}
          <div className="flex items-center gap-4 mb-6 text-sm text-muted">
            <span className="font-bold text-purple-500">{article.publication}</span>
            <span>•</span>
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              {article.date}
            </div>
            <span>•</span>
            <span>By {article.author}</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight mb-8 text-primary">
            {article.headline}
          </h1>

          {/* Content */}
          <div
            className="prose prose-lg max-w-none
              prose-headings:text-primary prose-headings:font-black prose-headings:tracking-tight
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
              prose-p:text-muted prose-p:leading-relaxed prose-p:mb-6
              prose-ul:text-muted prose-ul:my-6
              prose-li:mb-2
              prose-strong:text-primary"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* External Link */}
          {article.externalLink && article.externalLink !== '#' && (
            <div className="mt-12 pt-8 border-t border-themed">
              <a
                href={article.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-500 hover:text-purple-400 font-bold transition-colors"
              >
                Read Original Article <ExternalLink size={20} />
              </a>
            </div>
          )}

          {/* Share/Back */}
          <div className="mt-12 pt-8 border-t border-themed">
            <Link
              to="/recognition"
              className="inline-flex items-center gap-2 text-purple-500 hover:text-purple-400 transition-colors font-bold"
            >
              <ArrowLeft size={20} />
              View More Press Coverage
            </Link>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default BlogDetailPage;